import graphql__3ed23b858fc7b6e39198 from "./__generated__/AnswerOralInterview_answer.graphql.js";import { useCallback, useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";

import { useForm } from "../Form/withFormStore";
import { useFormatMessage } from "../Intl/Context";
import useOralInterviewAudioPlayback from "./useOralInterviewAudioPlayback";
import { ORAL_INTERVIEW } from "../Form/SchemaTypes";
import {
  ANSWER_MODE_VOICE,
  ANSWER_MODE_WRITTEN,
  MESSAGE_ROLE_USER,
  INTERVIEW_STATUS_COMPLETED,
  INTERVIEW_UI_STATE_ASSISTANT_SPEAKING,
  INTERVIEW_UI_STATE_IDLE,
  INTERVIEW_UI_STATE_READY_TO_SPEAK
} from "./oralInterviewUiTransitions";
import { oralInterviewMutationFeedback } from "./oralInterviewMutationUtils";
import Field, { FIELD_MODE_FIELDSET } from "../Form/Field";
import Spinner from "../Utils/Spinner";
import InterviewChatSection from "./InterviewChatSection";
import InterviewCompletionSection from "./InterviewCompletionSection";
import InterviewControlsSection from "./InterviewControlsSection";
import InterviewIntroSection from "./InterviewIntroSection";
import InterviewMicroCheckSection from "./InterviewMicroCheckSection";
import InterviewResponseSection from "./InterviewResponseSection";

import { startOralInterview } from "../../mutations/StartOralInterviewMutation";

const PRE_START_STEP_INTRO = "intro";
const PRE_START_STEP_MICRO_CHECK = "micro_check";

const AnswerOralInterview = props => {
  const [persistedAnswerReference, setPersistedAnswerReference] = useState(null);
  const answer = useFragment(
    AnswerOralInterview_answer,
    persistedAnswerReference || props.answer
  );
  const { content, status, messages, formField } = answer;
  const { id: formFieldId, fieldName, label, required, hide } = formField;
  const { parent, resourceId } = props;
  const { updateFieldValue } = useForm();
  const t = useFormatMessage();

  const genericErrorMessage = t(intlMessages.genericError);
  const networkErrorMessage = t(intlMessages.networkError);
  const pendingStartLabel = t(intlMessages.pendingStartInterview);

  const [preStartStep, setPreStartStep] = useState(PRE_START_STEP_INTRO);
  const [initialAnswerMode, setInitialAnswerMode] = useState(ANSWER_MODE_WRITTEN);
  const [initialMicCheckPassed, setInitialMicCheckPassed] = useState(false);

  const [interviewUiState, setInterviewUiState] = useState(INTERVIEW_UI_STATE_IDLE);
  const [isBusy, setIsBusy] = useState(false);
  const [pendingLabel, setPendingLabel] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const handleInterviewReadyToSpeak = useCallback(() => {
    setInterviewUiState(INTERVIEW_UI_STATE_READY_TO_SPEAK);
  }, []);

  const handleAssistantSpeaking = useCallback(() => {
    setFeedbackMessage(null);
    setInterviewUiState(INTERVIEW_UI_STATE_ASSISTANT_SPEAKING);
  }, []);

  const handleAssistantAudioUnavailable = useCallback(() => {
    setFeedbackMessage(t(intlMessages.assistantAudioUnavailable));
    setInterviewUiState(INTERVIEW_UI_STATE_READY_TO_SPEAK);
  }, [t]);

  const { playAssistantAudio, playCompletionAudio, stopAssistantAudioPlayback } =
    useOralInterviewAudioPlayback({
      onAssistantAudioUnavailable: handleAssistantAudioUnavailable,
      onAssistantSpeaking: handleAssistantSpeaking,
      onReadyToSpeak: handleInterviewReadyToSpeak
    });

  const handleBusyStateChanged = useCallback(nextIsBusy => {
    setIsBusy(nextIsBusy);

    if (nextIsBusy) setFeedbackMessage(null);
  }, []);

  const handleInterviewUiStateChanged = useCallback(nextUiState => {
    setInterviewUiState(nextUiState);
  }, []);

  const handleStartCompleted = useCallback(
    response => {
      setIsBusy(false);
      setPendingLabel(null);

      const result = response?.startOralInterview;
      const mutationFeedbackMessage = oralInterviewMutationFeedback(
        result,
        genericErrorMessage
      );

      setFeedbackMessage(mutationFeedbackMessage);
      if (!mutationFeedbackMessage) {
        setPersistedAnswerReference(result.answer);
        playAssistantAudio(result);
      }
    },
    [genericErrorMessage, playAssistantAudio]
  );

  const handleStartFailed = useCallback(() => {
    setIsBusy(false);
    setPendingLabel(null);
    setFeedbackMessage(networkErrorMessage);
  }, [networkErrorMessage]);

  const commitInterviewStart = useCallback(
    (answerMode, micCheckPassed) => {
      if (isBusy || !resourceId || !formFieldId) return;

      setInitialAnswerMode(answerMode);
      setInitialMicCheckPassed(micCheckPassed);
      setIsBusy(true);
      setPendingLabel(pendingStartLabel);
      setFeedbackMessage(null);

      startOralInterview(
        { resourceId, formFieldId },
        handleStartCompleted,
        handleStartFailed
      );
    },
    [
      formFieldId,
      handleStartCompleted,
      handleStartFailed,
      isBusy,
      pendingStartLabel,
      resourceId
    ]
  );

  const handleVoiceInterviewStarted = useCallback(() => {
    commitInterviewStart(ANSWER_MODE_VOICE, true);
  }, [commitInterviewStart]);

  const handleWrittenInterviewStarted = useCallback(() => {
    commitInterviewStart(ANSWER_MODE_WRITTEN, false);
  }, [commitInterviewStart]);

  const handleMicroCheckOpened = useCallback(() => {
    setPreStartStep(PRE_START_STEP_MICRO_CHECK);
  }, []);

  const isStarted = messages.length > 0;
  const hasUserMessage = messages.some(
    ({ role, text }) => role === MESSAGE_ROLE_USER && text?.trim().length > 0
  );
  const isCompleted = status === INTERVIEW_STATUS_COMPLETED;
  const schema = {
    type: ORAL_INTERVIEW,
    parent,
    validations: { required }
  };

  useEffect(() => {
    updateFieldValue(fieldName, { value: content || "", hasUserMessage });
  }, [content, fieldName, hasUserMessage, updateFieldValue]);

  return (
    <Field
      fieldName={fieldName}
      schema={schema}
      required={required}
      hide={hide}
      label={label}
      fieldMode={FIELD_MODE_FIELDSET}
    >
      <div className="margin-top-sm oral-interview">
        {!isStarted && (
          <>
            {preStartStep === PRE_START_STEP_INTRO && (
              <InterviewIntroSection
                answer={answer}
                onStartMicroCheck={handleMicroCheckOpened}
              />
            )}

            {preStartStep === PRE_START_STEP_MICRO_CHECK && (
              <InterviewMicroCheckSection
                resourceId={resourceId}
                onMicCheckPassed={handleVoiceInterviewStarted}
                onContinueInWriting={handleWrittenInterviewStarted}
                isBusy={isBusy}
              />
            )}
          </>
        )}

        {isStarted && !isCompleted && (
          <>
            <InterviewChatSection answer={answer} interviewUiState={interviewUiState} />

            <InterviewResponseSection
              answer={answer}
              resourceId={resourceId}
              initialAnswerMode={initialAnswerMode}
              initialMicCheckPassed={initialMicCheckPassed}
              interviewUiState={interviewUiState}
              onInterviewUiStateChange={handleInterviewUiStateChanged}
              isBusy={isBusy}
              onBusyStateChange={handleBusyStateChanged}
              playAssistantAudio={playAssistantAudio}
            />

            <InterviewControlsSection
              answer={answer}
              resourceId={resourceId}
              interviewUiState={interviewUiState}
              onInterviewUiStateChange={handleInterviewUiStateChanged}
              isBusy={isBusy}
              onBusyStateChange={handleBusyStateChanged}
              playCompletionAudio={playCompletionAudio}
              stopAssistantAudioPlayback={stopAssistantAudioPlayback}
            />
          </>
        )}

        {isCompleted && <InterviewCompletionSection answer={answer} />}

        {pendingLabel && (
          <p className="margin-top-xs">
            <Spinner className="inline-spinner margin-right-xxs" />
            {pendingLabel}
          </p>
        )}

        {feedbackMessage && (
          <p className="form-text text-danger margin-top-xs">{feedbackMessage}</p>
        )}
      </div>
    </Field>
  );
};

const AnswerOralInterview_answer = graphql__3ed23b858fc7b6e39198;

export default AnswerOralInterview;

const intlMessages = defineMessages({
  assistantAudioUnavailable: {
    id: "answer.oralInterview.assistantAudioUnavailable",
    defaultMessage:
      "L'audio de l'assistant est indisponible pour le moment. " +
      "L'entretien continue en texte."
  },
  genericError: {
    id: "answer.oralInterview.genericError",
    defaultMessage: "L'entretien vocal n'a pas pu être mis à jour."
  },
  networkError: {
    id: "answer.oralInterview.networkError",
    defaultMessage: "Une erreur réseau est survenue. Merci de réessayer."
  },
  pendingStartInterview: {
    id: "answer.oralInterview.pendingStartInterview",
    defaultMessage: "Démarrage de l'entretien..."
  }
});
